import type { PostType } from "./types.js";
import { POST_TYPE_REGISTRY } from "./registry.js";
import { buildZernioPostBody, type ZernioEnvelopeInput, type ZernioPostBody } from "./publish-envelope.js";
import { isVideoUrl } from "./media-format.js";
import { newScheduledPostId } from "./zernio-idempotency.js";

/**
 * Video companions: when a brand opts in, every single-video post also goes
 * out in extra formats — an Instagram Story, a Facebook Story, a YouTube
 * Short — at the same moment as the original post.
 *
 * This module holds the shared rules and the create loop used by every path
 * that hands a post to Zernio (schedule, publish-now, quick-post in apps/api,
 * and the auto-schedule worker). It stays free of @content-os/db and
 * @content-os/social: callers inject the model handles and the Zernio create
 * call, so the API and the worker run exactly the same logic.
 */

/** Formats a brand can opt into. Ordered as shown in settings. */
export const VIDEO_COMPANION_POST_TYPES = ["IG_STORY", "FB_STORY", "YOUTUBE_SHORT"] as const;
export type VideoCompanionPostType = (typeof VIDEO_COMPANION_POST_TYPES)[number];

export function isVideoCompanionPostType(value: unknown): value is VideoCompanionPostType {
  return typeof value === "string" && (VIDEO_COMPANION_POST_TYPES as readonly string[]).includes(value);
}

/** A companion row is marked in metadata so status mirroring can ignore it. */
export const VIDEO_COMPANION_METADATA_KEY = "videoCompanion";

export function isVideoCompanionRow(row: { metadata?: unknown } | null | undefined): boolean {
  const meta = row?.metadata as Record<string, unknown> | null | undefined;
  return meta?.[VIDEO_COMPANION_METADATA_KEY] === true;
}

/** "story" for Story formats, "feed" for everything else. */
export function postKind(postType: string | null | undefined, format?: string | null): "story" | "feed" {
  if (format === "STORY") return "story";
  if (postType && (POST_TYPE_REGISTRY as Record<string, { isStory?: boolean }>)[postType]?.isStory) {
    return "story";
  }
  return "feed";
}

/**
 * The one video a companion can re-post, or null. Stories and Shorts take a
 * single clip, so pieces with several media items (carousels) are skipped,
 * and a piece that is already a Story has nothing to add.
 */
export function companionSourceVideo(piece: {
  postType?: string | null;
  format?: string | null;
  mediaUrls?: string[] | null;
}): string | null {
  const urls = piece.mediaUrls ?? [];
  if (urls.length !== 1 || !isVideoUrl(urls[0])) return null;
  if (postKind(piece.postType, piece.format) === "story") return null;
  return urls[0];
}

const DAY_MS = 24 * 60 * 60 * 1000;

export interface VideoCompanionDeps {
  db: {
    scheduledPost: {
      find(filter: Record<string, unknown>, projection?: string): { lean(): Promise<any[]> };
      create(doc: Record<string, unknown>): Promise<any>;
    };
    piece: {
      find(filter: Record<string, unknown>, projection?: string): { lean(): Promise<any[]> };
    };
  };
  /**
   * Zernio createPost. Resolves to the new post id, or null when the call
   * failed. Must not throw. `scheduledPostId` is the id the row will be stored
   * under: send `zernioIdempotencyHeaders(scheduledPostId)` with the request so
   * a publish-sweep retry of the row reuses the same Idempotency-Key.
   */
  createPost(body: ZernioPostBody, opts: { scheduledPostId: string }): Promise<string | null>;
  log?: (message: string, err?: unknown) => void;
}

export interface CreateVideoCompanionsInput {
  brandId: string;
  pieceId: string;
  piece: ZernioEnvelopeInput["piece"] & { format?: string | null };
  /** The brand's opted-in companion formats (BrandSchedulingConfig.videoCompanionPostTypes). */
  enabledPostTypes: readonly string[] | null | undefined;
  /** The brand's ACTIVE social accounts. */
  accounts: ReadonlyArray<{ platform: string; zernioAccountId?: string | null }>;
  groupId: string;
  createdBy: unknown;
  /** When the original post goes out; companions go out at the same moment. */
  when: Date;
  publishNow: boolean;
}

/**
 * Create the companion posts for a video that was just handed to Zernio.
 *
 * A companion is skipped when its platform has no active account, or when
 * the same video is already scheduled or published on that platform in the
 * same kind (Story vs feed) within 24 hours. That window covers the parent
 * post created moments earlier (a YouTube video post suppresses a YouTube
 * Short companion), repeat scheduling of the same piece, and one video
 * spread across several per-platform pieces.
 *
 * Every companion is attempted independently: a failed Zernio call still
 * stores the row with a null zernioPostId so the publish sweep retries it,
 * and an unexpected error on one companion never blocks the others.
 */
export async function createVideoCompanionPosts(
  deps: VideoCompanionDeps,
  input: CreateVideoCompanionsInput,
): Promise<any[]> {
  const log = deps.log ?? ((message, err) => console.warn(message, err ?? ""));
  const enabled = [...new Set((input.enabledPostTypes ?? []).filter(isVideoCompanionPostType))];
  if (enabled.length === 0) return [];

  const videoUrl = companionSourceVideo(input.piece);
  if (!videoUrl) return [];

  const targets = enabled
    .map((postType) => {
      const platform = POST_TYPE_REGISTRY[postType as PostType].platform as string;
      const account = input.accounts.find((a) => a.platform === platform && a.zernioAccountId);
      return account ? { postType, platform, zernioAccountId: account.zernioAccountId as string } : null;
    })
    .filter((t): t is { postType: VideoCompanionPostType; platform: string; zernioAccountId: string } => t !== null);
  if (targets.length === 0) return [];

  const covered = await coveredPostKeys(deps, input, targets.map((t) => t.platform));

  const created: any[] = [];
  for (const target of targets) {
    const kind = postKind(target.postType);
    const key = coverageKey(target.platform, kind, videoUrl);
    if (covered.has(key)) continue;

    try {
      const body = buildZernioPostBody({
        piece: input.piece,
        account: { zernioAccountId: target.zernioAccountId, platform: target.platform },
        postTypeOverride: target.postType,
        ...(input.publishNow ? { publishNow: true } : { scheduledFor: input.when.toISOString() }),
      });
      const scheduledPostId = newScheduledPostId();
      const zernioPostId = await deps.createPost(body, { scheduledPostId });
      const row = await deps.db.scheduledPost.create({
        _id: scheduledPostId,
        pieceId: input.pieceId,
        brandId: input.brandId,
        platform: target.platform,
        groupId: input.groupId,
        zernioPostId,
        scheduledFor: input.when,
        postTypeOverride: target.postType,
        metadata: { [VIDEO_COMPANION_METADATA_KEY]: true, companionMediaUrl: videoUrl },
        createdBy: input.createdBy,
      });
      covered.add(key);
      created.push(row);
    } catch (err) {
      log(`[video-companions] ${target.postType} companion failed for piece ${input.pieceId}`, err);
    }
  }
  return created;
}

function coverageKey(platform: string, kind: "story" | "feed", url: string): string {
  return `${platform}|${kind}|${url}`;
}

/** Platform/kind/video combinations already posted around `when`. */
async function coveredPostKeys(
  deps: VideoCompanionDeps,
  input: CreateVideoCompanionsInput,
  platforms: string[],
): Promise<Set<string>> {
  const covered = new Set<string>();
  const rows = await deps.db.scheduledPost
    .find({
      brandId: input.brandId,
      platform: { $in: [...new Set(platforms)] },
      status: { $in: ["SCHEDULED", "PUBLISHED"] },
      scheduledFor: {
        $gte: new Date(input.when.getTime() - DAY_MS),
        $lte: new Date(input.when.getTime() + DAY_MS),
      },
    })
    .lean();
  if (rows.length === 0) return covered;

  const needPiece: any[] = [];
  for (const row of rows) {
    const companionUrl = (row.metadata as Record<string, unknown> | undefined)?.companionMediaUrl;
    if (typeof companionUrl === "string") {
      covered.add(coverageKey(row.platform, postKind(row.postTypeOverride), companionUrl));
    } else if (row.pieceId) {
      needPiece.push(row);
    }
  }
  if (needPiece.length === 0) return covered;

  const pieceIds = [...new Set(needPiece.map((row) => String(row.pieceId)))];
  const pieces = await deps.db.piece.find({ _id: { $in: pieceIds } }, "mediaUrls postType format").lean();
  const byId = new Map(pieces.map((p) => [String(p._id ?? p.id), p]));
  for (const row of needPiece) {
    const piece = byId.get(String(row.pieceId));
    if (!piece) continue;
    const kind = postKind(row.postTypeOverride ?? piece.postType, piece.format);
    for (const url of piece.mediaUrls ?? []) covered.add(coverageKey(row.platform, kind, url));
  }
  return covered;
}
